import React, { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { api, formatPrice } from "../../lib/api";
import { ProductImage } from "../../components/ProductImage";

const CATEGORIES = [
  { slug: "alcohol", name: "Alcool" },
  { slug: "soft_drinks", name: "Soft Drinks" },
  { slug: "water", name: "Eaux" },
  { slug: "snacks", name: "Snacks" },
  { slug: "planches", name: "Planches" },
  { slug: "restauration", name: "Restauration" },
  { slug: "tobacco", name: "Tabac" },
];

const SUBCATEGORIES = {
  alcohol: [
    { slug: "vodka_whisky", name: "Vodka & Whisky" },
    { slug: "beer", name: "Bière" },
    { slug: "wine_champagne", name: "Vin & Champagne" },
  ],
  restauration: [
    { slug: "pizzas", name: "Pizzas" },
    { slug: "pates", name: "Pâtes" },
    { slug: "desserts", name: "Desserts" },
    { slug: "trompe_loeil", name: "Trompe l'œil" },
  ],
};

const empty = {
  name: "",
  description: "",
  price: 0,
  category: "alcohol",
  subcategory: "",
  image_url: "",
  stock: 100,
  featured: false,
  active: true,
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/admin/products").then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(empty);
    setEditing("new");
  };

  const openEdit = (p) => {
    setForm({
      name: p.name,
      description: p.description,
      price: p.price,
      category: p.category,
      subcategory: p.subcategory || "",
      image_url: p.image_url,
      stock: p.stock,
      featured: p.featured,
      active: p.active,
    });
    setEditing(p.id);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : type === "number" ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = { ...form, subcategory: form.subcategory || null };
    if (editing === "new") {
      await api.post("/admin/products", payload);
    } else {
      await api.put(`/admin/products/${editing}`, payload);
    }
    setEditing(null);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await api.delete(`/admin/products/${id}`);
    load();
  };

  const filtered = filter === "all" ? products : products.filter((p) => p.category === filter);

  return (
    <div data-testid="admin-products-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow mb-2">Catalogue</p>
          <h1 className="font-display text-4xl text-white leading-none">
            Gestion <span className="italic text-gold">produits</span>
          </h1>
        </div>
        <button onClick={openNew} className="btn-gold" data-testid="add-product-btn">
          <Plus size={14} /> Nouveau produit
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setFilter("all")}
          className={`text-xs uppercase tracking-wider px-3 py-2 border ${
            filter === "all" ? "bg-[#d4af37] text-[#0a0f1a] border-[#d4af37]" : "border-subtle text-gray-300"
          }`}
        >
          Tous ({products.length})
        </button>
        {CATEGORIES.map((c) => {
          const count = products.filter((p) => p.category === c.slug).length;
          return (
            <button
              key={c.slug}
              onClick={() => setFilter(c.slug)}
              className={`text-xs uppercase tracking-wider px-3 py-2 border ${
                filter === c.slug ? "bg-[#d4af37] text-[#0a0f1a] border-[#d4af37]" : "border-subtle text-gray-300"
              }`}
            >
              {c.name} ({count})
            </button>
          );
        })}
      </div>

      <div className="bg-night-2 border border-subtle overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Chargement…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-subtle">
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-gray-500">
                <th className="p-4">Produit</th>
                <th className="p-4 hidden md:table-cell">Catégorie</th>
                <th className="p-4">Prix</th>
                <th className="p-4 hidden sm:table-cell">Stock</th>
                <th className="p-4">Statut</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <ProductImage src={p.image_url} alt={p.name} testId={`admin-product-image-${p.id}`}
                        className="w-12 h-12 flex-shrink-0" />
                      <div>
                        <p className="text-white">{p.name}</p>
                        {p.featured && <span className="text-[9px] uppercase tracking-wider text-[#d4af37]">Signature</span>}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell text-gray-400 capitalize">{p.category.replace("_", " ")}</td>
                  <td className="p-4 text-gold">{formatPrice(p.price)}</td>
                  <td className="p-4 hidden sm:table-cell text-gray-300">{p.stock}</td>
                  <td className="p-4">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${
                      p.active ? "border-emerald-500/40 text-emerald-400" : "border-gray-500/40 text-gray-400"
                    }`}>
                      {p.active ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button onClick={() => openEdit(p)} className="btn-ghost p-2" data-testid={`edit-product-${p.id}`}>
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDelete(p.id)} className="btn-ghost p-2 hover:text-red-400" data-testid={`delete-product-${p.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <>
          <div className="fixed inset-0 bg-black/70 z-40 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-5 pointer-events-none">
            <div className="bg-[#121824] border border-subtle w-full max-w-xl pointer-events-auto max-h-[90vh] overflow-y-auto" data-testid="product-edit-modal">
              <div className="flex items-center justify-between p-5 border-b border-subtle">
                <h3 className="font-display text-2xl text-white">
                  {editing === "new" ? "Nouveau produit" : "Modifier produit"}
                </h3>
                <button onClick={() => setEditing(null)} className="btn-ghost p-2"><X size={20} /></button>
              </div>
              <form onSubmit={handleSave} className="p-5 space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">Nom *</label>
                  <input required name="name" value={form.name} onChange={handleChange} className="input-line" data-testid="product-name-input" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">Description</label>
                  <textarea name="description" value={form.description} onChange={handleChange} rows={2} className="input-line resize-none border-b" />
                </div>
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">Prix (€) *</label>
                    <input required type="number" step="0.5" name="price" value={form.price} onChange={handleChange} className="input-line" data-testid="product-price-input" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">Stock</label>
                    <input type="number" name="stock" value={form.stock} onChange={handleChange} className="input-line" />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">Catégorie</label>
                  <select name="category" value={form.category} onChange={handleChange} className="input-line bg-[#0a0f1a]" data-testid="product-category-select">
                    {CATEGORIES.map((c) => (
                      <option key={c.slug} value={c.slug}>{c.name}</option>
                    ))}
                  </select>
                </div>
                {SUBCATEGORIES[form.category] && (
                  <div>
                    <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">Sous-catégorie</label>
                    <select name="subcategory" value={form.subcategory || ""} onChange={handleChange} className="input-line bg-[#0a0f1a]" data-testid="product-subcategory-select">
                      <option value="">— Aucune —</option>
                      {SUBCATEGORIES[form.category].map((s) => (
                        <option key={s.slug} value={s.slug}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">URL Image</label>
                  <input name="image_url" value={form.image_url} onChange={handleChange} className="input-line" placeholder="https://..." data-testid="product-image-url-input" />
                </div>
                <div className="flex flex-wrap gap-5">
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} className="accent-[#d4af37]" />
                    Produit signature
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input type="checkbox" name="active" checked={form.active} onChange={handleChange} className="accent-[#d4af37]" />
                    Actif
                  </label>
                </div>
                <div className="flex gap-3 pt-3 border-t border-subtle">
                  <button type="submit" className="btn-gold flex-1" data-testid="product-save-btn">Enregistrer</button>
                  <button type="button" onClick={() => setEditing(null)} className="btn-outline-gold">Annuler</button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminProducts;
